// app 1.5.27 (Alexander, 27 Oct; the mock-ups "M® Checkout — the first payment, handed to M", rounds 1–5) — the
// first-payment hand-off between M® Checkout's frame on a merchant's page and M's own tab.
//
// A browser keeps a frame's storage per top-level site, so the frame at Omega Air doesn't know the wallet M's own tab
// knows, nor what the frame at Ondas Coffee learnt. So when the frame has no wallet, its "Pay with M® ↗" is a real link
// to M's pay page in a named tab (?handoff=1): M's own page on globalmoneyapp.com, top level, where the passkey is
// picked once for every merchant. There the person pays; M's tab tells the frame — window.opener, M's origin only —
// that it is paid and which wallet paid it (the credential's PUBLIC values: id, public key, address; no secret), and
// closes itself. The frame keeps the wallet, so the next payment at that merchant is in place, one Touch ID.
//
// Both ends are M's origin. The merchant's page never sees the message (it is posted to M's frame with M's origin as
// the target), and the frame takes nothing from any other origin. Without an opener (a merchant page with
// Cross-Origin-Opener-Policy: same-origin cuts it) the frame still learns of the payment from /pay/status; only the
// remembering is lost.

export const HANDOFF = "m-handoff";

export interface HandoffCred { id: string; publicKey: string; rpId?: string; address?: string }
export interface HandoffMsg {
  m: typeof HANDOFF;
  event: "paid" | "settled";
  to: string; // the payee's wallet code, canonical
  ref: string | null;
  amount: string; // canonical, as the link carries it
  currency: string;
  userOpHash?: string | null;
  txHash?: string | null;
  cred?: HandoffCred | null;
  /** 1.5.48 — the M® card's public record and the limit, so the frame knows when a payment here needs the card
   *  (a frame's storage is the merchant's partition: it cannot see M's own). */
  card?: HandoffCard | null;
  cardAbove?: number | null;
}
/** 1.5.51 — the wallet a checkout's frame picked ("Choose another M® wallet") whose card the frame can't take: its public
 *  values ride M's tab's link as ?as=, so M's tab connects it with no prompt and takes the card there. Nothing secret. */
export interface AsCred { id: string; publicKey: string; rpId?: string; address: string }
export function encodeAs(c: AsCred): string {
  return btoa(JSON.stringify({ id: c.id, publicKey: c.publicKey, rpId: c.rpId, address: c.address })).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function decodeAs(s: string | null | undefined): AsCred | null {
  if (!s || s.length > 2000) return null;
  try {
    const j = JSON.parse(atob(s.replace(/-/g, "+").replace(/_/g, "/"))) as Partial<AsCred>;
    if (typeof j.id !== "string" || !j.id || typeof j.publicKey !== "string" || !/^0x[0-9a-fA-F]+$/.test(j.publicKey) || !isHex(j.address, 42)) return null;
    return { id: j.id, publicKey: j.publicKey, rpId: typeof j.rpId === "string" ? j.rpId : undefined, address: j.address! };
  } catch { return null; }
}
export interface HandoffCard { id: string; publicKey: string; rpId: string; address: string; kind: "card" | "key"; transports?: string[] }

/** The named tab a frame's link opens: one per order, so a second tap reuses it rather than opening another. */
export function handoffTarget(to: string, ref: string | null | undefined): string {
  return `mpay-${to}-${ref ?? ""}`.replace(/[^A-Za-z0-9-]/g, "").slice(0, 60);
}

const canon = (s: string) => s.toUpperCase().replace(/[^0-9A-Z]/g, "");
const isHex = (s: unknown, n?: number) => typeof s === "string" && /^0x[0-9a-fA-F]+$/.test(s) && (n == null || s.length === n);

/** The hand-off a frame accepts: from M's own origin, for exactly this order (payee, reference, amount, currency),
 *  with a credential only in the shape M keeps (an id, a public key, an address). Anything else → null. */
export function readHandoff(
  e: { origin: string; data: unknown },
  ownOrigin: string,
  want: { to: string; ref: string | null; amount: string; currency: string },
): HandoffMsg | null {
  if (e.origin !== ownOrigin) return null;
  const d = e.data as Partial<HandoffMsg> | null;
  if (!d || typeof d !== "object" || d.m !== HANDOFF) return null;
  if (d.event !== "paid" && d.event !== "settled") return null;
  if (typeof d.to !== "string" || canon(d.to) !== canon(want.to)) return null;
  if ((d.ref ?? null) !== (want.ref ?? null)) return null;
  if (typeof d.amount !== "string" || Number(d.amount) !== Number(want.amount)) return null;
  if (typeof d.currency !== "string" || d.currency.toUpperCase() !== want.currency.toUpperCase()) return null;
  let cred: HandoffCred | null = null;
  const c = d.cred as HandoffCred | null | undefined;
  if (c && typeof c.id === "string" && c.id.length > 0 && c.id.length < 400 && typeof c.publicKey === "string" && c.publicKey.length > 0 && c.publicKey.length < 1000
    && (c.address == null || isHex(c.address, 42)) && (c.rpId == null || typeof c.rpId === "string")) {
    cred = { id: c.id, publicKey: c.publicKey, rpId: c.rpId, address: c.address };
  }
  let card: HandoffCard | null = null;
  const k = d.card as HandoffCard | null | undefined;
  if (k && typeof k.id === "string" && k.id.length > 0 && k.id.length < 400 && typeof k.publicKey === "string" && k.publicKey.length > 0 && k.publicKey.length < 1000
    && isHex(k.address, 42) && typeof k.rpId === "string" && k.rpId.length < 200 && (k.kind === "card" || k.kind === "key")
    && (k.transports == null || (Array.isArray(k.transports) && k.transports.every((t) => typeof t === "string" && t.length < 20)))) {
    card = { id: k.id, publicKey: k.publicKey, rpId: k.rpId, address: k.address, kind: k.kind, transports: k.transports?.slice(0, 6) };
  }
  const cardAbove = typeof d.cardAbove === "number" && Number.isFinite(d.cardAbove) && d.cardAbove >= 0 ? d.cardAbove : null;
  return {
    m: HANDOFF, event: d.event, to: d.to, ref: d.ref ?? null, amount: d.amount, currency: d.currency,
    userOpHash: isHex(d.userOpHash, 66) ? d.userOpHash : null,
    txHash: isHex(d.txHash, 66) ? d.txHash : null,
    cred, card, cardAbove,
  };
}

/** M's tab → the frame that opened it: M's origin only. Quiet when there is no opener. */
export function sendHandoff(msg: Omit<HandoffMsg, "m">): void {
  try {
    const o = window.opener as Window | null;
    if (o && !o.closed) o.postMessage({ m: HANDOFF, ...msg }, location.origin);
  } catch { /* no opener, or cut by the merchant's page */ }
}

// ---- 1.5.55 — the Pay with M® button on a merchant's page (m.js), no frame -----------------------------------------
// m.js draws M's button on the merchant's page — a plain link to M's tab (?handoff=1, a named tab per order). M's tab
// pays on M's own origin and tells the page that opened it: paid (and settled once the receipt is in), in the frame's
// event shape ({ m: "pay", event, to, amount, currency, ref, userOpHash | txHash }). Nothing secret — no credential, no
// card: the merchant's page is not M's. The page's own events are for its screen; its server confirms with M.

/** 1.5.55 — the site whose checkout opened M's tab, as the browser says it: the referrer (the full address for a page on
 *  M's own origin — the demos — and the origin for another site), unless the referrer is M's own frame (/embed/), whose
 *  ?site= M wrote itself; then, and with no referrer at all (a page with no-referrer), the link's ?site=. The Worker
 *  checks the site against M's registry; a page that hides its referrer gets the check only for what it claims. */
export function tabSite(referrer: string, ownOrigin: string, param: string | null | undefined): string | undefined {
  const p = param ? param.slice(0, 300) : undefined;
  try {
    if (referrer) {
      const u = new URL(referrer);
      if (u.origin === ownOrigin && u.pathname.startsWith("/embed/")) return p;
      if (u.protocol === "https:" || u.protocol === "http:") return (u.origin + u.pathname).slice(0, 300);
    }
  } catch { /* fall through */ }
  return p;
}

export interface MerchantMsg { event: "paid" | "settled"; to: string; ref: string | null; amount: string; currency: string; userOpHash?: string | null; txHash?: string | null; viaPhone?: boolean }
/** 1.5.55 — M's tab for a checkout, as it first opened: the site (tabSite) and the origin to tell (the referrer's). Kept in
 *  the tab's sessionStorage by its path, because the M® card's trip to the approver comes back with the APPROVER as the
 *  referrer. Read once as the tab opens (main.ts), before any trip. */
export function tabContext(referrer: string, ownOrigin: string, param: string | null | undefined, store: Pick<Storage, "getItem" | "setItem"> | null, key: string): { site?: string; target: string } {
  try { const kept = store?.getItem(`m.tab.${key}`); if (kept) { const j = JSON.parse(kept) as { site?: string; target?: string }; return { site: j.site || undefined, target: j.target || "*" }; } } catch { /* compute */ }
  let target = "*";
  try { if (referrer) target = new URL(referrer).origin; } catch { /* "*" */ }
  const c = { site: tabSite(referrer, ownOrigin, param), target };
  try { store?.setItem(`m.tab.${key}`, JSON.stringify(c)); } catch { /* storage refused */ }
  return c;
}
let merchantTarget = "*";
export function setMerchantTarget(t: string): void { merchantTarget = t || "*"; }

/** 1.5.55 — M's tab → the merchant's page that opened it (m.js's button): the frame's event shape, nothing secret, to the
 *  origin of the page that opened the tab when the browser said it ("*" otherwise — the message carries only what the
 *  page already knows and hashes that are public on chain). Quiet when there is no opener. */
export function tellMerchant(msg: MerchantMsg): void {
  try {
    const o = window.opener as Window | null;
    if (!o || o.closed) return;
    o.postMessage({ m: "pay", viaTab: true, ...msg }, merchantTarget);
  } catch { /* cut by the page (Cross-Origin-Opener-Policy) */ }
}
